// T048: Summon Phase Handler

import type { GameState, Card, BattlefieldPosition, CardDefinition } from '../../types';
import { effectTriggerSystem } from '../effects/EffectTriggerSystem';
import { hasEffect } from '../effects/EffectTextParser';

export type SummonError =
  | 'CARD_NOT_IN_HAND'
  | 'SLOT_OCCUPIED'
  | 'INVALID_SIDE'
  | 'INVALID_POSITION';

export type SummonResult =
  | { success: true; state: GameState }
  | { success: false; error: SummonError };

const LITTLE_TRAVELER_ID = 'little_traveler';

/**
 * Get the slot at a given position.
 */
function getSlot(state: GameState, position: BattlefieldPosition) {
  const side = position.side === 'player1' ? state.battlefield.player1Side : state.battlefield.player2Side;
  const line = position.row === 'front' ? side.frontLine : side.backLine;
  return line[position.column - 1];
}

/**
 * Check if a slot is empty.
 */
function isSlotEmpty(state: GameState, position: BattlefieldPosition): boolean {
  const slot = getSlot(state, position);
  return slot.unit === null;
}

/**
 * Place a unit on the battlefield.
 * Copies effectIds from definition to activeEffects.
 */
function placeUnit(
  state: GameState,
  unit: Card,
  position: BattlefieldPosition,
  definitions?: Map<string, CardDefinition>
): GameState {
  const sideKey = position.side === 'player1' ? 'player1Side' : 'player2Side';
  const lineKey = position.row === 'front' ? 'frontLine' : 'backLine';

  // Get effects from definition
  const def = definitions?.get(unit.definitionId);
  const activeEffects = def?.effectIds ?? [];

  const newBattlefield = { ...state.battlefield };
  const newSide = { ...newBattlefield[sideKey] };
  const newLine = [...newSide[lineKey]];

  newLine[position.column - 1] = {
    ...newLine[position.column - 1],
    unit: {
      ...unit,
      location: 'battlefield',
      position,
      orientation: 'upright',
      activeEffects,
      summonedThisTurn: true, // 新召喚的單位無法攻擊（除非有偷襲）
    },
  };

  newSide[lineKey] = newLine;
  newBattlefield[sideKey] = newSide;

  return { ...state, battlefield: newBattlefield };
}

/**
 * Remove a card from player's hand.
 */
function removeFromHand(state: GameState, playerId: string, cardId: string): GameState {
  const isPlayer1 = playerId === state.player1.id;

  if (isPlayer1) {
    return {
      ...state,
      player1: {
        ...state.player1,
        hand: state.player1.hand.filter((c) => c.id !== cardId),
      },
    };
  } else {
    return {
      ...state,
      player2: {
        ...state.player2,
        hand: state.player2.hand.filter((c) => c.id !== cardId),
      },
    };
  }
}

/**
 * Summon a unit from hand to battlefield.
 */
export function summonUnit(
  state: GameState,
  playerId: string,
  cardId: string,
  position: BattlefieldPosition,
  definitions?: Map<string, CardDefinition>
): SummonResult {
  const isPlayer1 = playerId === state.player1.id;
  const player = isPlayer1 ? state.player1 : state.player2;
  const expectedSide = isPlayer1 ? 'player1' : 'player2';

  // Validate side
  if (position.side !== expectedSide) {
    return { success: false, error: 'INVALID_SIDE' };
  }

  // Find card in hand
  const card = player.hand.find((c) => c.id === cardId);
  if (!card) {
    return { success: false, error: 'CARD_NOT_IN_HAND' };
  }

  // Check slot is empty
  if (!isSlotEmpty(state, position)) {
    return { success: false, error: 'SLOT_OCCUPIED' };
  }

  // Remove from hand and place on battlefield
  let newState = removeFromHand(state, playerId, cardId);
  newState = placeUnit(newState, card, position, definitions);

  // 觸發召喚時效果
  if (definitions) {
    effectTriggerSystem.setDefinitions(definitions);
    const triggerResult = effectTriggerSystem.triggerOnSummon(newState, card, position);
    
    // 執行立即行動
    if (triggerResult.immediateActions.length > 0) {
      newState = effectTriggerSystem.executeActions(newState, triggerResult.immediateActions);
    }

    // 掩護 (Cover): check adjacent front-row units for cover keyword
    if (position.row === 'front') {
      const sideData = position.side === 'player1' ? newState.battlefield.player1Side : newState.battlefield.player2Side;
      const adjacentCols = [position.column - 1, position.column + 1].filter(c => c >= 1 && c <= 5);

      for (const adjCol of adjacentCols) {
        const neighbor = sideData.frontLine[adjCol - 1]?.unit;
        if (!neighbor) continue;

        // Check if neighbor has cover
        const neighborDef = definitions.get(neighbor.definitionId);
        const neighborEffectText = neighborDef?.effectText ?? '';
        if (!hasEffect(neighborEffectText, 'cover')) continue;

        // Check if back row behind cover unit is empty
        if (sideData.backLine[adjCol - 1]?.unit) continue;

        // Move summoned unit to back row behind cover unit
        const sideKey = position.side === 'player1' ? 'player1Side' : 'player2Side';
        const newBattlefield = { ...newState.battlefield };
        const newSide = { ...newBattlefield[sideKey] };

        // Remove from current front row position
        const newFrontLine = [...newSide.frontLine];
        newFrontLine[position.column - 1] = { ...newFrontLine[position.column - 1], unit: null };
        newSide.frontLine = newFrontLine;

        // Place in back row behind cover unit
        const newBackLine = [...newSide.backLine];
        const coverPosition: BattlefieldPosition = {
          side: position.side,
          row: 'back',
          column: adjCol as 1 | 2 | 3 | 4 | 5,
        };
        const movedCard: Card = {
          ...card,
          location: 'battlefield',
          position: coverPosition,
          orientation: 'upright',
          summonedThisTurn: true,
          coverProtected: true,
        };
        newBackLine[adjCol - 1] = { ...newBackLine[adjCol - 1], unit: movedCard };
        newSide.backLine = newBackLine;

        newBattlefield[sideKey] = newSide;
        newState = { ...newState, battlefield: newBattlefield };
        break; // Only one cover move per summon
      }
    }
  }

  return { success: true, state: newState };
}

/**
 * Summon a little traveler from the forest.
 */
export function summonTraveler(
  state: GameState,
  playerId: string,
  position: BattlefieldPosition
): SummonResult {
  const isPlayer1 = playerId === state.player1.id;
  const expectedSide = isPlayer1 ? 'player1' : 'player2';

  // Validate side
  if (position.side !== expectedSide) {
    return { success: false, error: 'INVALID_SIDE' };
  }

  // Check slot is empty
  if (!isSlotEmpty(state, position)) {
    return { success: false, error: 'SLOT_OCCUPIED' };
  }

  // Create little traveler card
  const traveler: Card = {
    id: `${LITTLE_TRAVELER_ID}_${String(Date.now())}`,
    definitionId: LITTLE_TRAVELER_ID,
    ownerId: playerId,
    location: 'battlefield',
    orientation: 'upright',
    position,
    summonedThisTurn: true, // 新召喚的單位無法攻擊
  };

  const newState = placeUnit(state, traveler, position);

  return { success: true, state: newState };
}

/**
 * Get valid summon positions for a player.
 */
export function getValidPositions(state: GameState, playerId: string): BattlefieldPosition[] {
  const side = playerId === state.player1.id ? 'player1' : 'player2';
  const positions: BattlefieldPosition[] = [];

  const rows: Array<'front' | 'back'> = ['front', 'back'];
  const columns: Array<1 | 2 | 3 | 4 | 5> = [1, 2, 3, 4, 5];

  for (const row of rows) {
    for (const column of columns) {
      const position: BattlefieldPosition = { side, row, column };
      if (isSlotEmpty(state, position)) {
        positions.push(position);
      }
    }
  }

  return positions;
}

/**
 * Check if player can summon any unit.
 */
export function canSummon(state: GameState, playerId: string): boolean {
  const player = playerId === state.player1.id ? state.player1 : state.player2;
  const validPositions = getValidPositions(state, playerId);

  return player.hand.length > 0 && validPositions.length > 0;
}

/**
 * Get cards that can be summoned from hand.
 */
export function getSummonableCards(
  state: GameState,
  playerId: string,
  definitions?: Map<string, CardDefinition>
): Card[] {
  const player = playerId === state.player1.id ? state.player1 : state.player2;

  // In summon phase, all unit cards can be summoned (including those requiring sacrifice)
  return player.hand.filter((card) => {
    if (!definitions) return true;
    const def = definitions.get(card.definitionId);
    return def?.type === 'unit';
  });
}

// Re-export as object for backwards compatibility
export const SummonPhaseHandler = {
  summonUnit,
  summonTraveler,
  getValidPositions,
  canSummon,
  getSummonableCards,
};
